import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, Input, InputNumber, message, Select, Space, Switch, Table, Tag, Typography } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import { api } from "../services/apiClient";
import { useSessionProfile } from "../services/SessionProfileContext";

const { Title, Text } = Typography;
const PLAN_MODES = new Set(["MANAGED", "BYOK", "DISABLED"]);

export function buildAiVideoEntitlementPatch(mode, limit) {
  if (!PLAN_MODES.has(mode)) throw new Error("AI_VIDEO_PLAN_INVALID");
  if (limit !== null && (!Number.isInteger(limit) || limit < -1)) throw new Error("AI_VIDEO_LIMIT_INVALID");
  return { aiVideoPlanMode: mode, aiVideoDailyLimit: limit };
}

function entitlementColor(mode) {
  return mode === "MANAGED" ? "green" : mode === "BYOK" ? "blue" : "default";
}

function AiVideoEntitlementEditor({ item, saving, onSave }) {
  const [mode, setMode] = useState(item.aiVideoPlanMode || "DISABLED");
  const [limit, setLimit] = useState(Number.isInteger(item.aiVideoDailyLimit) ? item.aiVideoDailyLimit : null);
  useEffect(() => {
    setMode(item.aiVideoPlanMode || "DISABLED");
    setLimit(Number.isInteger(item.aiVideoDailyLimit) ? item.aiVideoDailyLimit : null);
  }, [item.aiVideoDailyLimit, item.aiVideoPlanMode]);
  const save = () => {
    try { onSave(buildAiVideoEntitlementPatch(mode, limit)); }
    catch (_) { message.error("Hạn mức phải là -1, 0 hoặc một số nguyên dương."); }
  };
  return <Space direction="vertical" size={6}>
    <Select
      aria-label={`Gói AI Video của ${item.username || item.email || item.id}`}
      value={mode}
      style={{ width: 150 }}
      onChange={setMode}
      options={[
        { value: "MANAGED", label: "MANAGED" },
        { value: "BYOK", label: "BYOK" },
        { value: "DISABLED", label: "DISABLED" },
      ]}
    />
    <InputNumber
      aria-label={`Hạn mức AI Video của ${item.username || item.email || item.id}`}
      value={limit}
      min={-1}
      precision={0}
      placeholder="Mặc định hệ thống"
      onChange={setLimit}
      style={{ width: 150 }}
    />
    <Button size="small" type="link" style={{ padding: 0, textAlign: "left" }} onClick={() => setLimit(null)}>Dùng mặc định hệ thống</Button>
    <Text type="secondary">{limit === null ? "Đang dùng hạn mức hệ thống" : "-1 không giới hạn · 0 chặn tạo"}</Text>
    <Button size="small" type="primary" loading={saving} onClick={save}>Lưu gói AI Video</Button>
  </Space>;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = moment(value);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "-";
}

export default function SystemUsers() {
  const { profile, status } = useSessionProfile();
  const mounted = useRef(true);
  const loadGeneration = useRef(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState({});
  const isSuperAdmin = status === "ready" && profile.role === "SUPER_ADMIN";

  const load = useCallback(async () => {
    const request = ++loadGeneration.current;
    setLoading(true);
    try {
      const nextItems = await api.get("/api/users");
      if (mounted.current && request === loadGeneration.current) setItems(Array.isArray(nextItems) ? nextItems : []);
    } catch (error) {
      if (mounted.current && request === loadGeneration.current) message.error(error.message);
    } finally {
      if (mounted.current && request === loadGeneration.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    return () => { mounted.current = false; loadGeneration.current += 1; };
  }, [load]);

  const update = async (item, patch) => {
    if (saving[item.id]) return;
    setSaving((value) => ({ ...value, [item.id]: true }));
    try {
      await api.put(`/api/users/${item.id}`, patch);
      if (!mounted.current) return;
      message.success("Đã cập nhật user");
      await load();
    } catch (error) {
      if (mounted.current) message.error(error.message);
    } finally {
      if (mounted.current) setSaving((value) => { const next = { ...value }; delete next[item.id]; return next; });
    }
  };

  const data = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const roleOrder = { SUPER_ADMIN: 0, ADMIN: 1, USER: 2 };
    return items
      .filter((item) => !keyword || [item.username, item.email, item.id]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(keyword)))
      .sort((left, right) => {
        const roleDiff = (roleOrder[left.userType] ?? 3) - (roleOrder[right.userType] ?? 3);
        if (roleDiff !== 0) return roleDiff;
        return String(left.username || left.email || "").localeCompare(String(right.username || right.email || ""), "vi");
      });
  }, [items, search]);

  const columns = [
    {
      title: "USER",
      render: (_, item) => (
        <div>
          <Text strong>{item.username || "-"}</Text>
          <br />
          <Text type="secondary">{item.email || "-"}</Text>
        </div>
      ),
    },
    {
      title: "ROLE",
      dataIndex: "userType",
      width: 190,
      render: (value, item) => isSuperAdmin && value !== "SUPER_ADMIN" ? (
        <Select
          value={value || "USER"}
          style={{ width: 160 }}
          onChange={(role) => update(item, { userType: role })}
          options={[
            { value: "ADMIN", label: "ADMIN" },
            { value: "USER", label: "USER" },
          ]}
        />
      ) : <Tag color={value === "SUPER_ADMIN" ? "purple" : value === "ADMIN" ? "blue" : "default"}>{value || "USER"}</Tag>,
    },
    {
      title: "AI VIDEO",
      width: 210,
      render: (_, item) => isSuperAdmin ? (
        <AiVideoEntitlementEditor item={item} saving={Boolean(saving[item.id])} onSave={(patch) => update(item, patch)} />
      ) : <Space direction="vertical" size={2}>
        <Tag color={entitlementColor(item.aiVideoPlanMode)}>{item.aiVideoPlanMode || "DISABLED"}</Tag>
        <Text type="secondary">Hạn mức: {item.aiVideoDailyLimit === null || item.aiVideoDailyLimit === undefined ? "Mặc định hệ thống" : item.aiVideoDailyLimit === -1 ? "Không giới hạn" : item.aiVideoDailyLimit}</Text>
      </Space>,
    },
    {
      title: "TRẠNG THÁI",
      dataIndex: "isActive",
      width: 150,
      render: (value, item) => (
        <Switch
          checked={Boolean(value)}
          checkedChildren="ACTIVE"
          unCheckedChildren="INACTIVE"
          disabled={Boolean(saving[item.id])}
          onChange={(checked) => update(item, { isActive: checked })}
        />
      ),
    },
    { title: "LIMIT", dataIndex: "videoLimit", width: 100, render: (value) => value ?? "-" },
    { title: "ĐÃ DÙNG", dataIndex: "videoUsed", width: 100, render: (value) => value ?? 0 },
    { title: "NGÀY TẠO", dataIndex: "createdAt", width: 160, render: formatDateTime },
    { title: "CẬP NHẬT", dataIndex: "updatedAt", width: 160, render: formatDateTime },
  ];

  return (
    <>
      <div className="page-heading">
        <div>
          <Title level={3}>Quản lý người dùng</Title>
          <Text type="secondary">Quản lý role và trạng thái hoạt động của tài khoản.</Text>
        </div>
      </div>
      <Card className="criclebox">
        <div className="auto-toolbar">
          <Input className="auto-toolbar-search" allowClear prefix={<SearchOutlined />} placeholder="Tìm theo username hoặc email" value={search} onChange={(event) => setSearch(event.target.value)} />
          <Button icon={<ReloadOutlined />} onClick={load}>Làm mới</Button>
        </div>
        <Table rowKey="id" loading={loading} columns={columns} dataSource={data} />
      </Card>
    </>
  );
}
